"use client";

import { useEffect, useState } from "react";
import { ArrowUpRight, Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-10">
        <div className="h-4 w-14 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-14 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-14 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-14 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6 lg:gap-10">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative text-foreground font-default text-xs uppercase tracking-[0.2em] font-bold bg-transparent hover:bg-transparent focus:bg-transparent px-0 py-1 group">
                <span className="relative inline-block">
                  {item.label}
                  <span className="absolute left-0 -bottom-1 h-px w-0 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-8 pt-12">
        <div className="h-8 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-44 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-6 px-8 pt-4">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col gap-6">
          <Link to={item.href || "#"} onClick={onNavigate} className="font-default text-3xl text-foreground hover:text-primary transition-colors duration-300 uppercase font-semibold">
            {item.label}
          </Link>
          {i < menuItems.length - 1 && <div className="flex items-center gap-3">
              <span className="h-px flex-1 bg-border" />
              <span className="text-primary text-xs tracking-widest">
                &#8226;
              </span>
              <span className="h-px flex-1 bg-border" />
            </div>}
        </div>)}
    </nav>;
}
export default function MainNavigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="4399" id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-500 ease-out ${scrolled ? "bg-background/90 backdrop-blur-md border-b border-border" : "bg-background border-b border-transparent"}`}>
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex items-center justify-between py-6">
          {}
          <div className="flex-1 flex items-center">
            <Link to="/" aria-label="GEROI - Home" className="inline-flex items-center">
              <WvcLogo className="h-7 w-auto text-foreground" />
            </Link>
          </div>

          {}
          <div className="hidden lg:flex flex-1 items-center justify-center">
            <MenuProvider menu_id="18">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex-1 flex items-center justify-end gap-3">
            <Button asChild variant="outline" className="hidden sm:inline-flex group rounded-md border-foreground/20 bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 px-6 h-11 font-default font-bold uppercase tracking-widest text-xs">
              <Link to="/#contact">
                <span>Analisi Export</span>
                <ArrowUpRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" aria-hidden="true" />
              </Link>
            </Button>

            {}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden text-foreground hover:bg-accent rounded-md" aria-label="Open menu">
                  <Menu className="size-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background border-l border-border w-full sm:max-w-md p-0">
                <SheetTitle className="sr-only">Menu di Navigazione</SheetTitle>
                <div className="flex h-full flex-col">
                  <div className="px-8 pt-8 pb-6">
                    <Link to="/" onClick={() => setMobileOpen(false)} aria-label="GEROI - Home" className="inline-flex items-center">
                      <WvcLogo className="h-7 w-auto text-foreground" />
                    </Link>
                  </div>

                  <div className="flex items-center gap-3 px-8 pb-2">
                    <span className="h-px flex-1 bg-border" />
                    <span className="font-default text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold">
                      Intelligence
                    </span>
                    <span className="h-px flex-1 bg-border" />
                  </div>

                  <div className="flex-1 overflow-y-auto py-8">
                    <MenuProvider menu_id="18">
                      <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                    </MenuProvider>
                  </div>

                  <div className="px-8 pb-10 pt-6 border-t border-border">
                    <Button asChild variant="outline" className="w-full group rounded-md border-foreground/20 bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 h-12 font-default font-bold uppercase tracking-widest">
                      <Link to="/#contact" onClick={() => setMobileOpen(false)}>
                        <span>Richiedi Consulenza</span>
                        <ArrowUpRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" aria-hidden="true" />
                      </Link>
                    </Button>
                    <p className="mt-4 font-default text-[10px] uppercase tracking-[0.25em] text-muted-foreground text-center font-medium">
                      Black Label Strategy &#183; B2B Intelligence
                    </p>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}